import React from 'react';
import { Modal } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { useCompanyRegistrationDE } from 'components/Registration/useCompanyRegistrationDE';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { useEmail } from 'hooks/useEmail';
import { usePhoneNumber } from 'hooks/usePhoneNumber';
import { useSearchParams } from 'react-router-dom';
import { ASSET_PREFIX } from 'utils/assets/assets';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './HelpModal.module.css';

type Props = {
  showModal: boolean;
  closeModal: () => void;
};

export const HelpModal: React.FunctionComponent<Props> = ({
  showModal,
  closeModal,
}) => {
  const [searchParams] = useSearchParams();
  const { trackEvent } = useAmplitude();
  const { introducerType, introducerCompany } = useIntroducerData();
  const isAccountant =
    introducerType === 'accountant' ||
    searchParams.get('introducerType') === 'accountant';
  // Get company name from URL parameters
  const { company: companyNameFromURLParams } = useCompanyRegistrationDE();

  const companyName =
    introducerCompany?.public_facing_company_name ||
    introducerCompany?.company_name ||
    companyNameFromURLParams?.name;

  const { phoneNumber, phoneNumberLink } = usePhoneNumber(companyName);
  const { email } = useEmail(isAccountant, companyName);
  const handlePhoneNumberTracking = () => {
    trackEvent(AMPLITUDE_EVENTS.contactLinkClicked, {
      'CTA location': 'Help modal',
      'CTA destination': 'Call us',
      'CTA text': 'Call us',
      'CTA URL': `tel:${phoneNumberLink}`,
    });
  };

  const handleEmailTracking = () => {
    trackEvent(AMPLITUDE_EVENTS.contactLinkClicked, {
      'CTA location': 'Help modal',
      'CTA destination': 'Email us',
      'CTA text': 'Email us',
      'CTA URL': `mailto:${email}`,
    });
  };

  return (
    <Modal
      closeIcon
      headerBar
      isOpen={showModal}
      onClose={closeModal}
      title={t`Need help?`}
    >
      <div className={styles.mainContent}>
        <picture>
          <img
            src={`${ASSET_PREFIX}/personWearingHeadset.jpg`}
            className={styles.image}
            alt={t`Person wearing a headset`}
          />
        </picture>
        <div className={styles.informationContent}>
          <p className={styles.callUsText}>
            {t`Call us Monday to Friday (9am - 6pm)`}
          </p>
          <p className={styles.phoneNumber}>
            <a
              href={`tel:${phoneNumberLink}`}
              onClick={handlePhoneNumberTracking}
              data-testid="call-link"
            >
              {phoneNumber}
            </a>
          </p>
          <p className={styles.emailUsText}>{t`Email us at`}</p>
          <div>
            <a
              href={`mailto:${email}`}
              onClick={handleEmailTracking}
              data-testid="email-link"
            >
              {email}
            </a>
          </div>
        </div>
      </div>
    </Modal>
  );
};
